import { useCallback } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import {
	performGetRequest,
	performPostRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { AddUserRolesResponse, UserRoleType, UserRolesResponse } from './types.tsx';

const getUserRolesService = (projectKey?: string) => {
	if (!projectKey) {
		throw new Error('Project Key is required to fetch user roles');
	}

	return performGetRequest<UserRolesResponse>(
		`/rest/api/3/project/${projectKey}/roledetails?excludeConnectAddons=true`,
	);
};

type AddUserRoleParams = {
	projectKey?: string;
	roleId: number;
	groups: string[];
	users: string[];
};

const addUserRoleService = ({
	projectKey,
	roleId,
	groups,
	users,
}: {
	projectKey?: string;
	roleId: number;
	groups: string[];
	users: string[];
}) => {
	if (!projectKey) {
		throw new Error('Project key is required to add user role');
	}

	return performPostRequest<AddUserRolesResponse>(
		`/rest/projectconfig/latest/roles/${projectKey}/${roleId}`,
		{
			body: JSON.stringify({ groups, users }),
		},
	);
};

export const useUserRole = (projectKey?: string) => {
	const {
		data,
		fetch,
		loading: isFetching,
		error: fetchError,
	} = useService<UserRoleType[]>(() => getUserRolesService(projectKey));

	const {
		loading: isAdding,
		error: addError,
		fetch: addUserRoleInternal,
	} = useService<AddUserRolesResponse, AddUserRoleParams>(addUserRoleService);

	const add = useCallback(
		(roleId: number, { users, groups }: { users: string[]; groups: string[] }) =>
			addUserRoleInternal({ projectKey, roleId, users, groups }),
		[addUserRoleInternal, projectKey],
	);

	return {
		fetch,
		add,
		data,
		loading: isFetching || isAdding,
		error: fetchError || addError,
	};
};
